import { GLOBAL_ERROR } from "@/common/constant/global-error";
import { ButtonColor } from "@/common/enum/button-color";
import { DuplicateDiscountModal } from "@/components/modal/DuplicateDiscountModal";
import { userService } from "@/services/user-service";
import { DiscountDto } from "@/types/dto/discount-dto";
import { ItemDetailDto } from "@/types/dto/item-detail-dto";
import { UserDto } from "@/types/dto/user-dto";
import { Button, Table } from "flowbite-react";
import moment from "moment";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { RemoveDiscountModal } from "../../modal/RemoveDiscountModal";

interface Props {
  headersLength: number;
  openIncentivesModal: () => void;
  disabled?: boolean;
  items: ItemDetailDto[];
  checkedItemIds: number[];
  user: UserDto;
  getPage: (searchItemName: string) => void;
  searchItemName: string;
  discounts: DiscountDto[];
}

export const PartnerItemTableFooter: React.FC<Props> = ({
  headersLength,
  openIncentivesModal,
  disabled,
  checkedItemIds,
  user,
  discounts,
}) => {
  const navigate = useNavigate();
  const [isOpenRemoveModal, setIsOpenRemoveModal] = useState(false);
  const [isOpenDuplicateModal, setIsOpenDuplicateModal] = useState(false);
  const [partnerCount, setPartnerCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isDuplicateLoading, setIsDuplicateLoading] = useState(false);

  const incentiveItems = checkedItemIds.filter((id) =>
    discounts.some(
      (discount) =>
        discount.item?.id === id &&
        (discount.endDate === undefined ||
          discount.endDate === null ||
          moment(discount.endDate).isAfter(moment()))
    )
  );

  const isRemoveDiscountButtonEnabled =
    incentiveItems.length === checkedItemIds.length &&
    checkedItemIds.length > 0;

  const isDuplicateButtonEnabled = checkedItemIds.length > 0;

  const handleRemoveDiscount = async () => {
    try {
      setIsLoading(true);
      const response = await userService.incentiveDiscounts(user.id, {
        fromDate: undefined,
        toDate: undefined,
        discountRate: 0,
        itemIds: checkedItemIds,
        delete: true,
      });

      if (!response.ok) {
        alert(GLOBAL_ERROR);
        return;
      }
      navigate(0);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
      setIsOpenRemoveModal(false);
    }
  };

  const handleOpenDuplicateModal = async () => {
    try {
      setIsLoading(true);
      const gradeId = user.grade.id;
      const response = await userService.countPartnersByGrade(gradeId);

      if (response.ok) {
        setPartnerCount(response.data.count);
        setIsOpenDuplicateModal(true);
      } else {
        alert("파트너 수 조회에 실패했습니다.");
      }
    } catch (error) {
      console.error(error);
      alert("파트너 수 조회에 실패했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDuplicateDiscount = async () => {
    try {
      setIsDuplicateLoading(true);
      const gradeId = user.grade.id;

      const itemsWithDiscount = checkedItemIds.filter((itemId) =>
        discounts.some(
          (discount) =>
            discount.item?.id === itemId &&
            (discount.endDate === undefined ||
              discount.endDate === null ||
              moment(discount.endDate).isAfter(moment()))
        )
      );

      const discountPayloads: Array<{
        discountRate: number;
        fromDate?: string;
        toDate?: string;
        itemIds: number[];
      }> = [];

      for (const itemId of itemsWithDiscount) {
        const discount = discounts.find(
          (d) =>
            d.item?.id === itemId &&
            (d.endDate === undefined ||
              d.endDate === null ||
              moment(d.endDate).isAfter(moment()))
        );

        if (!discount) {
          continue;
        }

        let fromDate: string | undefined;
        if (discount.startDate) {
          fromDate = moment(discount.startDate).toISOString();
        } else {
          fromDate = moment().toISOString();
        }

        let toDate: string | undefined;
        if (discount.endDate) {
          toDate = moment(discount.endDate).toISOString();
        } else {
          toDate = undefined;
        }

        discountPayloads.push({
          discountRate: discount.discountRate,
          fromDate,
          toDate,
          itemIds: [itemId],
        });
      }

      if (discountPayloads.length === 0) {
        alert("적용할 할인 정보가 없습니다.");
        setIsOpenDuplicateModal(false);
        return;
      }

      const response = await userService.applyDiscountToGrade(
        gradeId,
        discountPayloads
      );

      if (response.ok) {
        const { totalAffectedPartners, totalAffectedItems, results } =
          response.data;

        const successItems = results.filter((r) => r.success);
        const failedItems = results.filter((r) => !r.success);

        if (successItems.length > 0) {
          alert(
            `총 ${totalAffectedItems}개 상품 할인 요청이 ${totalAffectedPartners}명의 파트너에게 성공적으로 처리되었습니다.${
              failedItems.length > 0
                ? `\n${failedItems.length}개 상품은 적용에 실패했습니다.`
                : ""
            }`
          );
        } else {
          alert("할인 복제에 실패했습니다.");
        }
      } else {
        alert("할인 복제에 실패했습니다.");
      }

      setIsOpenDuplicateModal(false);
    } catch (error) {
      console.error(error);
      alert("할인 복제 중 오류가 발생했습니다.");
      setIsOpenDuplicateModal(false);
    } finally {
      setIsDuplicateLoading(false);
    }
  };

  return (
    <>
      <Table.Row className="bg-white sticky bottom-[-1px] h-[70px]">
        <Table.Cell colSpan={headersLength + 1} className="border">
          <div className="flex justify-between">
            <div className="p-[16px] flex gap-4">
              <Button
                size="sm"
                color={
                  disabled || isLoading
                    ? ButtonColor.DISABLED
                    : ButtonColor.TRANSPARENT_OUTLINE
                }
                onClick={openIncentivesModal}
                disabled={disabled || isLoading}
              >
                선택 상품 할인
              </Button>
              <Button
                size="sm"
                color={
                  !isRemoveDiscountButtonEnabled || isLoading
                    ? ButtonColor.DISABLED
                    : ButtonColor.TRANSPARENT_OUTLINE
                }
                onClick={() => setIsOpenRemoveModal(true)}
                disabled={!isRemoveDiscountButtonEnabled || isLoading}
              >
                {isLoading ? "처리중..." : "선택 상품 할인 제거"}
              </Button>
              <Button
                size="sm"
                color={
                  !isDuplicateButtonEnabled || isLoading
                    ? ButtonColor.DISABLED
                    : ButtonColor.TRANSPARENT_OUTLINE
                }
                onClick={handleOpenDuplicateModal}
                disabled={!isDuplicateButtonEnabled || isLoading}
              >
                {isLoading ? "처리중..." : "선택 상품 할인 복제"}
              </Button>
            </div>
          </div>
        </Table.Cell>
      </Table.Row>
      {isOpenRemoveModal && (
        <RemoveDiscountModal
          handleSubmit={handleRemoveDiscount}
          closeModal={() => setIsOpenRemoveModal(false)}
          isLoading={isLoading}
        />
      )}
      {isOpenDuplicateModal && (
        <DuplicateDiscountModal
          handleSubmit={handleDuplicateDiscount}
          closeModal={() => setIsOpenDuplicateModal(false)}
          partnerCount={partnerCount}
          partnerGrade={user.grade.name}
          isLoading={isDuplicateLoading}
        />
      )}
    </>
  );
};
